/**
 * Проекты: список для хаба и создание.
 *
 * Сгенерированный клиент отдаёт всё опциональным (так устроен protojson: поле
 * со значением по умолчанию в ответ не попадает), а экрану нужны обычные
 * значения. Приведение — здесь, чтобы дальше по коду не было ни `?? ''`, ни
 * `?.` на каждом обращении.
 */
import { projectApi } from '@/api'
import type {
  V1MemberRole,
  V1Project,
  V1StatusCategory,
  V1TypeProfile,
} from '@/api/clients/tracker-project'

export interface Project {
  id: string
  /** Префикс ключей задач: DEV, SALES */
  key: string
  name: string
  description: string
  /** Цвет точки проекта; пусто — экран берёт цвет по умолчанию */
  color: string
  isPrivate: boolean
  openIssueCount: number
  /** Задач в колонках категории «в работе» — второе число на карточке хаба */
  inProgressIssueCount: number
  /** Пусто у живого проекта; иначе — когда его убрали из хаба */
  archivedAt: string
  /** Несколько участников для аватаров: список отдаёт их вместе с проектом */
  members: ProjectMember[]
  /** Профиль типов задач: он решает, какие типы вообще есть в проекте */
  typeProfile: V1TypeProfile | ''
  /** Роль вошедшего; без роли он проекту посторонний */
  myRole: V1MemberRole | ''
}

export interface ProjectMember {
  id: string
  name: string
}

export interface CreateProjectPayload {
  key: string
  name: string
  description: string
  typeProfile: V1TypeProfile
  isPrivate: boolean
}

const toProject = (project: V1Project): Project => ({
  id: project.id ?? '',
  key: project.key ?? '',
  name: project.name ?? '',
  description: project.description ?? '',
  color: project.color ?? '',
  isPrivate: project.isPrivate ?? false,
  // Счётчик приходит строкой: в protobuf это int64, а он не влезает в number.
  openIssueCount: Number(project.openIssueCount ?? 0),
  inProgressIssueCount: Number(project.inProgressIssueCount ?? 0),
  archivedAt: project.archivedAt ?? '',
  members: (project.members ?? []).map((member) => ({
    id: member.userId ?? '',
    name: member.displayName ?? '',
  })),
  typeProfile: project.typeProfile ?? '',
  myRole: project.myRole ?? '',
})

export interface ListProjectsParams {
  onlyMine?: boolean
  search?: string
  /** Архивные вместо живых: в хабе это отдельная вкладка */
  archived?: boolean
}

export const listProjects = async ({
  onlyMine,
  search,
  archived,
}: ListProjectsParams = {}): Promise<Project[]> => {
  // Системные проекты — хранилище задач сделок и клиентов, людям в хабе они
  // не нужны и участников у них нет.
  const { data } = await projectApi.projectServiceListProjects(
    onlyMine,
    false,
    search || undefined,
    undefined,
    undefined,
    archived,
  )

  return (data.projects ?? []).map(toProject)
}

/**
 * Просьба о доступе к приватному проекту. Повторное нажатие второго
 * уведомления не поднимает — это решает сервис (DEV-1493).
 */
export const requestProjectAccess = async (key: string): Promise<void> => {
  await projectApi.projectServiceRequestProjectAccess(key, {})
}

/** Убрать проект из хаба или вернуть обратно. Задачи остаются на месте. */
export const setProjectArchived = async (id: string, archived: boolean): Promise<Project> => {
  const { data } = await projectApi.projectServiceUpdateProject(id, { archived })

  return toProject(data.project ?? {})
}

/**
 * Один проект по ключу из адреса (`/p/DEV/board`) или по идентификатору.
 *
 * Экрану проекта нужен именно запрос, а не поиск в загруженном списке: список
 * отвечает на «какие проекты у меня есть», а здесь важно отличить «нет такого»
 * от «есть, но не для вас» — это разные экраны, и знает разницу только сервер.
 */
export const getProject = async (idOrKey: string): Promise<Project> => {
  const { data } = await projectApi.projectServiceGetProject(idOrKey)

  return toProject(data.project ?? {})
}

/**
 * Участники проекта — для аватаров на карточке. Отдельным запросом на проект:
 * списком их не отдают, а аватары в макете есть.
 */
export const listMembers = async (projectId: string): Promise<ProjectMember[]> => {
  const { data } = await projectApi.projectServiceListProjectMembers(projectId)

  // Имя — только настоящее: сейчас tracker его не заполняет (DEV-1499), и
  // подставлять вместо него идентификатор значит рисовать в аватаре две
  // случайные цифры из UUID. Пустое имя дизайн-система показывает как «?».
  return (data.members ?? []).map((member) => ({
    id: member.userId ?? '',
    name: member.displayName ?? '',
  }))
}

export interface ProjectColumn {
  id: string
  name: string
  /** Цвет колонки; он же цвет пилюли статуса в списке */
  color: string
  statusCategory: V1StatusCategory | ''
}

/**
 * Колонки проекта отдельным запросом — списку задач они нужны как статусы:
 * задача приезжает с `column_id`, а имя и цвет статуса живут в колонке.
 * Спрашивать вместо этого доску значит тянуть все её карточки ради заголовков.
 */
export const listColumns = async (projectId: string): Promise<ProjectColumn[]> => {
  const { data } = await projectApi.projectServiceListBoardColumns(projectId)

  return (data.columns ?? []).map((column) => ({
    id: column.id ?? '',
    name: column.name ?? '',
    color: column.color ?? '',
    statusCategory: column.statusCategory ?? '',
  }))
}

/**
 * Пресет колонок доски идёт за профилем типов: у разработки шесть колонок с
 * ревью и тестированием, у простого проекта три. Спрашивать про это отдельно
 * значит спрашивать дважды об одном.
 */
export const createProject = async (payload: CreateProjectPayload): Promise<Project> => {
  const { data } = await projectApi.projectServiceCreateProject({
    key: payload.key,
    name: payload.name,
    description: payload.description,
    typeProfile: payload.typeProfile,
    isPrivate: payload.isPrivate,
    preset:
      payload.typeProfile === 'TYPE_PROFILE_DEVELOPMENT'
        ? 'COLUMN_PRESET_DEVELOPMENT'
        : 'COLUMN_PRESET_SIMPLE',
  })

  return toProject(data.project ?? {})
}
